// src/lib/observability/posthog.ts
import { Capacitor } from "@capacitor/core";
import posthog from "posthog-js";

let started = false;
let lastAppOpenAt = 0;

const OPT_OUT_KEY = "gounique:analytics_opt_out";

function isOptedOut(): boolean {
  try {
    return localStorage.getItem(OPT_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Whether analytics is currently enabled (default true; user can opt out). */
export function isAnalyticsEnabled(): boolean {
  return !isOptedOut();
}

/** Initialize PostHog on the native app only. No-op on web / no key / opted out. */
export function initPostHog(): void {
  if (started) return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!Capacitor.isNativePlatform() || !key) return;
  if (isOptedOut()) return;
  started = true;
  posthog.init(key, {
    defaults: "2026-01-30", // pin SDK defaults — no silent behavior drift on upgrade
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    persistence: "localStorage", // capacitor://localhost cookies are unreliable
    person_profiles: "identified_only", // GDPR-friendlier, no anon profiles
    disable_session_recording: true,
  });
}

/** Attach (or clear) the Supabase user id on PostHog events. No email / PII. */
export function setPostHogUser(id: string | null): void {
  if (!started) return;
  if (id) posthog.identify(id);
  else posthog.reset();
}

export function captureEvent(name: string, props?: Record<string, unknown>): void {
  if (!started || isOptedOut()) return; // no-op on web / before init / in tests / opted out
  posthog.capture(name, props);
}

/** app_open with a small dedupe so cold-start + initial appStateChange don't double-count. */
export function captureAppOpen(): void {
  if (!started || isOptedOut()) return;
  const now = Date.now();
  if (now - lastAppOpenAt < 2000) return;
  lastAppOpenAt = now;
  captureEvent("app_open");
}

/** Persist the user's analytics preference and apply it at runtime. */
export function setAnalyticsEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(OPT_OUT_KEY, enabled ? "0" : "1");
  } catch {
    /* ignore */
  }
  if (!started) {
    if (enabled) initPostHog(); // first opt-in after boot
    return;
  }
  if (enabled) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
    posthog.reset(); // clear the pseudonymous device identifier (policy §9)
  }
}
